/**
 * useDropdownAccessibility
 *
 * Adds full keyboard navigation and accessibility support for dropdown and flyout menus.
 *
 * - Supports arrow key navigation (Up, Down, Left, Right), Enter, and Escape
 * - Handles focus movement between menu button, flyout menus, and options
 * - Differentiates logic for DROPDOWN vs FLYOUT types
 * - Automatically cleans up listeners on unmount
 *
 * Designed to support nested/flyout menus at any level of depth.
 */
import { onKeyStroke, useActiveElement } from "@vueuse/core";
import { computed, nextTick } from "vue";
import { focusNextElement, focusPreviousElement, getTabbableElements } from "@/common/helpers/keyboard-navigation";
export var MenuItemType;
(function (MenuItemType) {
    MenuItemType["FLYOUT"] = "FLYOUT";
    MenuItemType["DROPDOWN"] = "DROPDOWN";
    MenuItemType["DEFAULT"] = "DEFAULT";
})(MenuItemType || (MenuItemType = {}));
export const useDropdownAccessibility = (args) => {
    const { menuItemBtnType, submenu, menuToggleBtn, toggleSubMenu, eventTargetEl, isOpen, menu } = args;
    const activeElement = useActiveElement();
    // Handle both raw HTMLElement or Vue component instance
    const menuEl = computed(() => {
        const raw = menu?.value;
        return raw ? ("$el" in raw ? raw.$el : raw) : null;
    });
    const submenuEl = computed(() => {
        const raw = submenu?.value;
        const element = raw ? ("$el" in raw ? raw.$el : raw) : null;
        // Only enable submenu behavior for flyout menus when focused on flyout buttons
        if (element && menuItemBtnType === MenuItemType.FLYOUT) {
            const isFlyoutButtonFocused = activeElement.value?.classList.contains("rec-dropdown-menu__item--flyout");
            return isFlyoutButtonFocused ? element : null;
        }
        // For other menu types, no submenu behavior needed
        return null;
    });
    const isRadioButton = (element) => {
        return element?.tagName === "INPUT" && element?.getAttribute("type") === "radio";
    };
    const handleArrowDown = async (e) => {
        if (isRadioButton(activeElement.value)) {
            return;
        }
        e.preventDefault();
        if (menuEl.value) {
            e.stopPropagation();
        }
        // In dropdown: open menu if focused on button, then focus first item
        if (menuItemBtnType === MenuItemType.DROPDOWN) {
            if (!isOpen.value) {
                toggleSubMenu();
                await nextTick();
            }
            // Try getting the first submenu-item and if submenu is closed/not available get the first focusable menu-item
            const [first] = getTabbableElements(submenuEl.value ?? menuEl.value);
            if (isRadioButton(first)) {
                first.checked = true;
                first?.focus();
                return;
            }
            //If not radio button, focus the first item in the submenu
            submenuEl.value && focusNextElement([submenuEl.value], activeElement.value);
        }
        // If already open, just cycle focus inside the menu
        menuEl.value && focusNextElement([menuEl.value], activeElement.value);
    };
    const handleArrowUp = (e) => {
        if (isRadioButton(activeElement.value)) {
            return;
        }
        e.preventDefault();
        if (menuEl.value) {
            e.stopPropagation();
        }
        menuEl.value && focusPreviousElement([menuEl.value], activeElement.value);
    };
    // Only relevant for flyout menus — close submenu or re-open from button
    const handleArrowRight = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (menuItemBtnType !== MenuItemType.DROPDOWN) {
            if (!isOpen.value && menuItemBtnType === MenuItemType.FLYOUT) {
                toggleSubMenu();
                await nextTick();
            }
            submenuEl.value && focusNextElement([submenuEl.value], activeElement.value);
        }
    };
    // Only relevant for flyout menus — close submenu or re-open from button
    const handleArrowLeft = async (e) => {
        e.preventDefault();
        if (menuItemBtnType !== MenuItemType.DROPDOWN) {
            menuToggleBtn.value?.focus();
            if (isOpen.value) {
                e.stopPropagation();
                toggleSubMenu();
            }
        }
    };
    const handleEnterOrSpace = async (e) => {
        if (menuItemBtnType === MenuItemType.DEFAULT)
            return;
        if (e.target !== menuToggleBtn.value && !submenuEl.value)
            return;
        e.stopPropagation();
        e.preventDefault();
        if (!isOpen.value) {
            toggleSubMenu();
            await nextTick();
            submenuEl.value && focusNextElement([submenuEl.value], activeElement.value);
        }
        else {
            toggleSubMenu();
        }
    };
    // Closes only one level of menu if using nested menus
    const handleEscape = async (e) => {
        e.preventDefault();
        menuToggleBtn.value?.focus();
        if (isOpen.value) {
            e.stopPropagation();
            toggleSubMenu();
        }
    };
    const keyMap = {
        ArrowDown: handleArrowDown,
        ArrowUp: handleArrowUp,
        ArrowRight: handleArrowRight,
        ArrowLeft: handleArrowLeft,
        Enter: handleEnterOrSpace,
        Escape: handleEscape,
        " ": handleEnterOrSpace
    };
    // Cleanup func to clean up key event listeners by caller
    return onKeyStroke(Object.keys(keyMap), (e) => {
        const handler = keyMap[e.key];
        handler?.(e);
    }, { target: eventTargetEl });
};
